import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ShoppingCart, Trash2, Tag, Loader2, CreditCard,
  Upload, CheckCircle2, ArrowRight, Wallet, Building2,
  DollarSign, X, Crown, Shield, Award
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizerCart = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ items: [], total: 0, discount: 0, promo_code: null });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [removingItem, setRemovingItem] = useState(null);
  
  // Checkout state
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentType, setPaymentType] = useState('paypal'); // 'paypal' or 'manual'
  const [paymentMethod, setPaymentMethod] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [proofImage, setProofImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [paypalProcessing, setPaypalProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cartRes, methodsRes] = await Promise.all([
        axios.get(`${API_URL}/api/organizer/cart`),
        axios.get(`${API_URL}/api/payment-methods/public`)
      ]);
      setCart(cartRes.data);
      setPaymentMethods(methodsRes.data);
      if (methodsRes.data.length > 0) {
        setPaymentMethod(methodsRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    setRemovingItem(itemId);
    try {
      const response = await axios.delete(`${API_URL}/api/organizer/cart/remove/${itemId}`);
      setCart(response.data.cart);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    } finally {
      setRemovingItem(null);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    setPromoValidating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/organizer/cart/apply-promo?code=${encodeURIComponent(promoCode.toUpperCase())}`
      );
      setCart(response.data.cart);
      toast.success(`Promo code "${promoCode.toUpperCase()}" applied!`);
      setPromoCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoValidating(false);
    }
  };

  const handleRemovePromo = async () => {
    try {
      const response = await axios.delete(`${API_URL}/api/organizer/cart/remove-promo`);
      setCart(response.data.cart);
      toast.success('Promo code removed');
    } catch (error) {
      toast.error('Failed to remove promo code');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCheckout = async () => {
    const selectedMethod = paymentMethods.find(m => m.id === paymentMethod);
    if (!referenceNumber && selectedMethod?.type !== 'cash') {
      toast.error('Please enter the reference number');
      return;
    }

    setSubmitting(true);
    try {
      let url = `${API_URL}/api/organizer/cart/checkout?payment_method=${paymentMethod}`;
      if (referenceNumber) url += `&reference_number=${encodeURIComponent(referenceNumber)}`;
      if (proofImage) url += `&payment_proof=${encodeURIComponent(proofImage)}`;
      
      await axios.post(url);
      setCheckoutOpen(false);
      setConfirmationOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setSubmitting(false);
    }
  };

  // PayPal handlers
  const createPayPalOrder = async () => {
    try {
      setPaypalProcessing(true);
      const planId = cart.items[0]?.plan?.id;
      if (!planId) throw new Error('No plan in cart');
      
      const response = await axios.post(`${API_URL}/api/paypal/create-order`, {
        plan_id: planId,
        promo_code: cart.promo_code
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to PayPal approval URL
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        throw new Error('No approval URL received');
      }
    } catch (error) {
      console.error('PayPal create order error:', error);
      toast.error('Failed to create PayPal order');
      setPaypalProcessing(false);
    }
  };

  const onPayPalApprove = async (data) => {
    setPaypalProcessing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/paypal/execute-payment?payment_id=${data.paymentID}&payer_id=${data.payerID}`
      );
      
      toast.success(response.data.message);
      setCheckoutOpen(false);
      setConfirmationOpen(true);
      
      // Clear cart
      await axios.delete(`${API_URL}/api/organizer/cart/clear`);
      setCart({ items: [], total: 0, discount: 0, promo_code: null });
    } catch (error) {
      console.error('PayPal execute error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setPaypalProcessing(false);
    }
  };

  const onPayPalError = (err) => {
    console.error('PayPal error:', err);
    toast.error('PayPal payment failed. Please try again.');
  };

  // Handle free checkout (when promo code makes total = 0)
  const handleFreeCheckout = async () => {
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setSubmitting(true);
    try {
      const planId = cart.items[0]?.plan?.id;
      const response = await axios.post(
        `${API_URL}/api/organizer/subscribe`,
        null,
        {
          params: {
            plan_id: planId,
            promo_code: cart.promo_code,
            payment_method: 'promo_code'
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Clear cart after successful subscription
      await axios.delete(`${API_URL}/api/organizer/cart/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCart({ items: [], total: 0, discount: 0, promo_code: null });
      
      if (response.data.auto_approved) {
        toast.success('🎉 Subscription activated! Enjoy your premium features.');
        setConfirmationOpen(true);
      } else {
        toast.success('Subscription request submitted!');
        setConfirmationOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getMethodIcon = (type) => {
    switch (type) {
      case 'paypal': return <Wallet className="h-5 w-5 text-blue-500" />;
      case 'gcash': return <Wallet className="h-5 w-5 text-blue-400" />;
      case 'bank': return <Building2 className="h-5 w-5 text-green-500" />;
      case 'cash': return <DollarSign className="h-5 w-5 text-amber-500" />;
      default: return <CreditCard className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'organized': return <Crown className="h-5 w-5 text-blue-600" />;
      case 'transparency': return <Shield className="h-5 w-5 text-purple-600" />;
      case 'license': return <Award className="h-5 w-5 text-orange-600" />;
      default: return <CreditCard className="h-5 w-5 text-gray-600" />;
    }
  };

  const selectedPaymentMethod = paymentMethods.find(m => m.id === paymentMethod);
  const finalTotal = Math.max(0, cart.total - cart.discount);

  if (loading) {
    return (
      <OrganizerLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </OrganizerLayout>
    );
  }

  return (
    <OrganizerLayout>
      <div className="space-y-6" data-testid="organizer-cart">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <ShoppingCart className="h-8 w-8" />
              Shopping Cart
            </h1>
            <p className="text-gray-600 mt-1">
              {cart.items?.length || 0} item(s) in your cart
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/pricing')}
          >
            Continue Shopping
          </Button>
        </div>

        {cart.items?.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingCart className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">Your cart is empty</h2>
              <p className="text-gray-500 mb-6">Browse our plans and add one to get started</p>
              <Button onClick={() => navigate('/pricing')} className="bg-maroon hover:bg-maroon-hover text-white">
                View Plans
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex items-center p-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                        {getPlanIcon(item.plan?.tier)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg">{item.plan?.name}</h3>
                        <p className="text-sm text-gray-500">{item.plan?.tagline}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {item.plan?.max_members === -1 ? 'Unlimited' : item.plan?.max_members} attendees
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {item.plan?.price_type === 'yearly' ? 'Yearly' : 'One-time'}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-maroon">{formatCurrency(item.price)}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={removingItem === item.id}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-1"
                        >
                          {removingItem === item.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><Trash2 className="h-4 w-4 mr-1" /> Remove</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Promo Code */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Promo Code
                    </Label>
                    {cart.promo_code ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-green-500 text-white">
                            {cart.promo_code}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={handleRemovePromo}
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-green-600 mt-1">
                          Discount: -{formatCurrency(cart.discount)}
                        </p>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="font-mono uppercase"
                        />
                        <Button
                          variant="outline"
                          onClick={handleApplyPromo}
                          disabled={promoValidating || !promoCode.trim()}
                        >
                          {promoValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Price Breakdown */}
                  <div className="space-y-2 pt-4 border-t">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span>{formatCurrency(cart.total)}</span>
                    </div>
                    {cart.discount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount</span>
                        <span>-{formatCurrency(cart.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                      <span>Total</span>
                      <span className="text-maroon">{formatCurrency(finalTotal)}</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => finalTotal === 0 ? handleFreeCheckout() : setCheckoutOpen(true)}
                    className="w-full bg-maroon hover:bg-maroon-hover text-white"
                    data-testid="checkout-btn"
                  >
                    {finalTotal === 0 ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Activate Free Subscription
                      </>
                    ) : (
                      <>
                        Proceed to Checkout
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Payment Methods Preview */}
              {paymentMethods.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">Accepted Payment Methods</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {paymentMethods.map(method => (
                        <div key={method.id} className="flex items-center gap-1 text-sm text-gray-600">
                          {getMethodIcon(method.type)}
                          <span>{method.name}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-maroon flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Complete Payment
            </DialogTitle>
            <DialogDescription>
              Total: {formatCurrency(finalTotal)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Payment Type Selection */}
            <div className="space-y-2">
              <Label>Choose Payment Method</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={paymentType === 'paypal' ? 'default' : 'outline'}
                  className={paymentType === 'paypal' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => setPaymentType('paypal')}
                >
                  <Wallet className="h-4 w-4 mr-2" />
                  PayPal
                </Button>
                <Button
                  type="button"
                  variant={paymentType === 'manual' ? 'default' : 'outline'}
                  className={paymentType === 'manual' ? 'bg-maroon hover:bg-maroon-hover' : ''}
                  onClick={() => setPaymentType('manual')}
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Manual
                </Button>
              </div>
            </div>

            {/* PayPal Payment */}
            {paymentType === 'paypal' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-700 mb-2">
                    <Wallet className="h-5 w-5" />
                    <span className="font-medium">PayPal Checkout</span>
                  </div>
                  <p className="text-sm text-blue-600">
                    You will be redirected to PayPal to complete your payment securely.
                  </p>
                </div>
                
                {paypalProcessing ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-blue-600">Redirecting to PayPal...</span>
                  </div>
                ) : (
                  <Button
                    onClick={createPayPalOrder}
                    className="w-full bg-[#0070ba] hover:bg-[#003087] text-white h-12 text-lg"
                    disabled={!cart.items?.length}
                  >
                    <Wallet className="h-5 w-5 mr-2" />
                    Pay with PayPal
                  </Button>
                )}
              </div>
            )}

            {/* Manual Payment */}
            {paymentType === 'manual' && (
              <>
                {paymentMethods.length > 0 && (
                  <div className="space-y-2">
                    <Label>Payment Method</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentMethods.map(method => (
                          <SelectItem key={method.id} value={method.id}>
                            <span className="flex items-center gap-2">
                              {getMethodIcon(method.type)}
                              {method.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Payment Instructions */}
                {selectedPaymentMethod && (
                  <div className={`rounded-lg p-4 text-sm ${
                    selectedPaymentMethod.type === 'paypal' ? 'bg-blue-50' :
                    selectedPaymentMethod.type === 'gcash' ? 'bg-blue-50' :
                    selectedPaymentMethod.type === 'bank' ? 'bg-green-50' :
                    'bg-amber-50'
                  }`}>
                    <p className="font-semibold mb-2">Payment Instructions:</p>
                    <p className="font-bold text-lg mb-2">
                      Amount to Pay: {formatCurrency(finalTotal)}
                    </p>
                    {selectedPaymentMethod.account_name && (
                      <p>Account Name: <strong>{selectedPaymentMethod.account_name}</strong></p>
                    )}
                    {selectedPaymentMethod.account_number && (
                      <p>Account Number: <strong>{selectedPaymentMethod.account_number}</strong></p>
                    )}
                    {selectedPaymentMethod.account_email && (
                      <p>Email: <strong>{selectedPaymentMethod.account_email}</strong></p>
                    )}
                    {selectedPaymentMethod.bank_name && (
                      <p>Bank: <strong>{selectedPaymentMethod.bank_name}</strong></p>
                    )}
                    {selectedPaymentMethod.instructions && (
                      <p className="mt-2 text-gray-600">{selectedPaymentMethod.instructions}</p>
                    )}
                  </div>
                )}

                {selectedPaymentMethod?.type !== 'cash' && (
                  <>
                    <div className="space-y-2">
                      <Label>Reference Number</Label>
                      <Input
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        placeholder="Enter transaction reference number"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Proof of Payment (Optional)</Label>
                      <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                          id="proof-upload"
                        />
                        <label htmlFor="proof-upload" className="cursor-pointer">
                          {proofImage ? (
                            <img src={proofImage} alt="Proof" className="max-h-32 mx-auto rounded" />
                          ) : (
                            <div className="text-gray-500">
                              <Upload className="h-8 w-8 mx-auto mb-2" />
                              <p className="text-sm">Upload screenshot</p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setCheckoutOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCheckout}
                    disabled={submitting}
                    className="flex-1 bg-maroon hover:bg-maroon-hover text-white"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Submit for Verification
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmationOpen} onOpenChange={setConfirmationOpen}>
        <DialogContent className="max-w-md text-center">
          <div className="py-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <DialogTitle className="font-heading text-2xl text-maroon mb-4">
              {finalTotal === 0 ? '🎉 Subscription Activated!' : paymentType === 'paypal' ? 'Payment Successful!' : 'Order Submitted!'}
            </DialogTitle>
            <p className="text-gray-600 mb-6">
              {finalTotal === 0 
                ? 'Your subscription has been activated instantly! Enjoy all the premium features.'
                : paymentType === 'paypal' 
                  ? 'Your payment has been processed and your subscription is now active!'
                  : 'Your subscription request is being verified by our admin team. You\'ll receive a notification once approved.'
              }
            </p>
            {paymentType === 'manual' && finalTotal > 0 && (
              <p className="text-sm text-gray-500 mb-6">
                This usually takes 1-2 business hours during office hours.
              </p>
            )}
            <Button 
              onClick={() => {
                setConfirmationOpen(false);
                navigate('/organizer');
              }}
              className="bg-maroon hover:bg-maroon-hover text-white"
            >
              Go to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </OrganizerLayout>
  );
};

export default OrganizerCart;
